'use client'

import { ReactNode } from 'react'
import { Card, Cell, Divider, Spacer, Text, Toggle } from '@vinted/web-ui'

import { useCatalogContext } from '@marketplace-web/catalog/catalog-page-data'

import { useProactiveModerationContext } from '../../context'

import SendToModeration from '../SendToModeration'

const FIRST_ITEMS_COUNT = 20

type Props = {
  isSendToAlertsShown: boolean
  isRoleMarketingPhotos: boolean
  marketingSection: ReactNode
}

const ProactiveModeration = ({
  isSendToAlertsShown,
  isRoleMarketingPhotos,
  marketingSection,
}: Props) => {
  const {
    state: {
      items: { items = [] },
    },
  } = useCatalogContext()

  const {
    isProactiveModerationEnabled,
    toggleProactiveModeration,
    selectedItemIds,
    setSelectedItemIds,
  } = useProactiveModerationContext()

  const isAllSelected = selectedItemIds === null
  const firstItems = items.slice(0, FIRST_ITEMS_COUNT).map(item => item.id)

  const handleSelectAll = () => {
    setSelectedItemIds(null)
  }

  const handleSelectFirstItems = () => {
    setSelectedItemIds(firstItems)
  }

  const handleManualSelect = () => {
    setSelectedItemIds([])
  }

  const renderMarketingSection = () => {
    if (!isRoleMarketingPhotos) return null

    return marketingSection
  }

  const renderSelectOptions = () => {
    const isFirstItemsSelected = selectedItemIds?.join(',') === firstItems.join(',')
    const isManualSelected = !isAllSelected && !isFirstItemsSelected

    return (
      <div>
        <button type="button" onClick={handleSelectAll}>
          <Text
            as="p"
            text="Select all listings"
            type={Text.Type.Subtitle}
            highlight={isAllSelected}
            underline={isAllSelected}
            clickable={!isAllSelected}
          />
        </button>
        <Spacer size={Spacer.Size.Large} orientation={Spacer.Orientation.Vertical} />
        <button type="button" onClick={handleSelectFirstItems}>
          <Text
            as="p"
            text="Select first 20 listings"
            type={Text.Type.Subtitle}
            underline={isFirstItemsSelected}
            highlight={isFirstItemsSelected}
            clickable={!isFirstItemsSelected}
          />
        </button>
        <Spacer size={Spacer.Size.Large} orientation={Spacer.Orientation.Vertical} />
        <button type="button" onClick={handleManualSelect}>
          <Text
            as="p"
            text="Select listings manually"
            type={Text.Type.Subtitle}
            underline={isManualSelected}
            highlight={isManualSelected}
            clickable={!isManualSelected}
          />
        </button>
      </div>
    )
  }

  const renderActions = () => {
    if (!selectedItemIds) {
      return <SendToModeration variant="all" />
    }

    return <SendToModeration variant="selected" />
  }

  const renderProactiveModerationToggle = () => {
    if (!isSendToAlertsShown) return null

    return (
      <Toggle
        name="proactive_moderation"
        text="Proactive moderation"
        onChange={toggleProactiveModeration}
        checked={isProactiveModerationEnabled}
      />
    )
  }

  const renderProactiveModerationOptions = () => {
    if (!isProactiveModerationEnabled) return null

    return (
      <>
        <Spacer size={Spacer.Size.Medium} />
        <Divider />
        <Spacer size={Spacer.Size.Medium} />
        <div className="u-flexbox u-align-items-center u-justify-content-between">
          {renderSelectOptions()}
          <div>{renderActions()}</div>
        </div>
      </>
    )
  }

  if (!isSendToAlertsShown && !isRoleMarketingPhotos) return null

  return (
    <Card>
      <div className="u-ui-padding-medium">
        <Cell
          prefix={renderMarketingSection()}
          suffix={renderProactiveModerationToggle()}
          styling={Cell.Styling.Tight}
          theme="transparent"
        />
        {renderProactiveModerationOptions()}
      </div>
    </Card>
  )
}

export default ProactiveModeration
